import {
  Accordion,
  Avatar,
  Badge,
  Card,
  Divider,
  Group,
  List,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { format } from "date-fns";
import type {
  AdminOwnerDetailOutput,
  AdminTrainerDetailOutput,
} from "@/api-types";

interface AccountDetailsProps {
  account: AdminTrainerDetailOutput | AdminOwnerDetailOutput;
  accountType: "trainer" | "owner";
}

export const AccountDetails = ({
  account,
  accountType,
}: AccountDetailsProps) => {
  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group gap="md">
          {account.portraitFileName && (
            <Avatar
              src={`/api/files/${account.portraitFileName}`}
              size="lg"
              radius="md"
            />
          )}
          <div>
            <Title order={3}>Account Details</Title>
            <Text size="sm" c="dimmed">
              {account.firstName} {account.lastName}
            </Text>
          </div>
        </Group>
        <Badge
          color={account.status === "NORMAL" ? "green" : "red"}
          variant="light"
        >
          {account.status}
        </Badge>
      </Group>

      <SimpleGrid cols={2} spacing="md">
        <Card withBorder p="md">
          <Stack gap="xs">
            <Text fw={600} size="sm">
              Personal Information
            </Text>
            <Text size="sm">
              <strong>Name:</strong> {account.firstName} {account.lastName}
            </Text>
            <Text size="sm">
              <strong>Email:</strong> {account.email}
            </Text>
            <Text size="sm">
              <strong>Phone:</strong> {account.phone}
            </Text>
            <Text size="sm">
              <strong>Location:</strong> {account.locality}
            </Text>
            {accountType === "trainer" &&
              "address" in account &&
              account.address && (
                <Text size="sm">
                  <strong>Address:</strong> {account.address}
                </Text>
              )}
            {accountType === "trainer" &&
              "postalCode" in account &&
              account.postalCode && (
                <Text size="sm">
                  <strong>Postal Code:</strong> {account.postalCode}
                </Text>
              )}
            <Text size="sm">
              <strong>Created:</strong>{" "}
              {format(new Date(account.createdAt), "MMM dd, yyyy")}
            </Text>
            <Text size="sm">
              <strong>Updated:</strong>{" "}
              {format(new Date(account.updatedAt), "MMM dd, yyyy")}
            </Text>
          </Stack>
        </Card>

        <Card withBorder p="md">
          <Stack gap="xs">
            <Text fw={600} size="sm">
              Account Info
            </Text>
            <Text size="sm">
              <strong>ID:</strong> {account.id}
            </Text>
            <Text size="sm">
              <strong>External ID:</strong> {account.externalId}
            </Text>
            {account.inviteCode && (
              <Text size="sm">
                <strong>Invite Code:</strong> {account.inviteCode}
              </Text>
            )}
          </Stack>
        </Card>
      </SimpleGrid>

      {accountType === "trainer" && (
        <Accordion variant="separated">
          {"publicProfile" in account && account.publicProfile && (
            <Accordion.Item value="profile">
              <Accordion.Control>Public Profile</Accordion.Control>
              <Accordion.Panel>
                <Stack gap="xs">
                  {account.publicProfile.headline && (
                    <Text size="sm">
                      <strong>Headline:</strong>{" "}
                      {account.publicProfile.headline}
                    </Text>
                  )}
                  {account.publicProfile.description && (
                    <Text size="sm">
                      <strong>Description:</strong>{" "}
                      {account.publicProfile.description}
                    </Text>
                  )}
                  {account.publicProfile.specialities &&
                    account.publicProfile.specialities.length > 0 && (
                      <div>
                        <Text size="sm" fw={600}>
                          Specialities:
                        </Text>
                        <List size="sm">
                          {account.publicProfile.specialities.map(
                            (spec: string, i: number) => (
                              <List.Item key={i}>{spec}</List.Item>
                            ),
                          )}
                        </List>
                      </div>
                    )}
                  {account.publicProfile.languages &&
                    account.publicProfile.languages.length > 0 && (
                      <Text size="sm">
                        <strong>Languages:</strong>{" "}
                        {account.publicProfile.languages.join(", ")}
                      </Text>
                    )}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          )}

          {"sessionTypes" in account &&
            account.sessionTypes &&
            account.sessionTypes.length > 0 && (
              <Accordion.Item value="sessions">
                <Accordion.Control>
                  Session Types ({account.sessionTypes.length})
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="xs">
                    {account.sessionTypes.map((session) => (
                      <Card key={session.id} withBorder p="sm">
                        <Text fw={600} size="sm">
                          {session.name}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {session.durationMinutes} min • €
                          {(session.price / 100).toFixed(2)}
                        </Text>
                        {session.description && (
                          <Text size="xs">{session.description}</Text>
                        )}
                      </Card>
                    ))}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            )}

          {"groupClassTypes" in account &&
            account.groupClassTypes &&
            account.groupClassTypes.length > 0 && (
              <Accordion.Item value="classes">
                <Accordion.Control>
                  Group Classes ({account.groupClassTypes.length})
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="xs">
                    {account.groupClassTypes.map((cls) => (
                      <Card key={cls.id} withBorder p="sm">
                        <Text fw={600} size="sm">
                          {cls.name}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {cls.sessionCount} sessions • {cls.durationMinutes}{" "}
                          min each • €{(cls.price / 100).toFixed(2)} per{" "}
                          {cls.pricePer.toLowerCase()}
                        </Text>
                        {cls.description && (
                          <Text size="xs">{cls.description}</Text>
                        )}
                      </Card>
                    ))}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            )}
        </Accordion>
      )}

      {accountType === "owner" &&
        "dogs" in account &&
        account.dogs &&
        account.dogs.length > 0 && (
          <Card withBorder p="md">
            <Stack gap="xs">
              <Text fw={600}>Dogs ({account.dogs.length})</Text>
              <Divider />
              {account.dogs.map((dog) => (
                <Group key={dog.id} justify="space-between">
                  <Group gap="sm">
                    {dog.portraitFileName && (
                      <Avatar
                        src={`/api/files/${dog.portraitFileName}`}
                        size="sm"
                      />
                    )}
                    <div>
                      <Text fw={500} size="sm">
                        {dog.name}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {dog.breed}
                      </Text>
                    </div>
                  </Group>
                  <Text size="xs" c="dimmed">
                    Born: {dog.birthDate}
                  </Text>
                </Group>
              ))}
            </Stack>
          </Card>
        )}
    </Stack>
  );
};
